/**
 * formTaskAssignmentValidator — shared between v3Validate.js + v4Validate.js.
 *
 * Single source of truth for form_task assignment-shape validation. Mirrors
 * the backend AssigneeResolverService.AssigneeRef contract (RC-219) so the
 * FE and BE agree on what's allowed before the canvas ever PUTs a save.
 *
 * Why a shared module exists. RC-220 originally inlined the same ~150-line
 * validator into both v3Validate and v4Validate. That hits the "no
 * band-aids" guardrail — a fix in one validator file would silently miss
 * the other. Extracting here means RC-222 / RC-223 (when new constraints
 * land for DYNAMIC / EXTERNAL_SUBJECT) only update one file.
 *
 * Public API:
 *   validateFormTaskAssignment(node, issues)
 *     — pushes 0+ issues onto the supplied `issues` array. Idempotent.
 *     — node is a v3 OR v4 canvas node; only n.id, n.label, n.type, n.config
 *       are read, all of which are shape-compatible between the two formats.
 *
 *   ASSIGNEE_TYPE_CONSTANTS — re-exported so the Form Designer picker
 *     (RC-221) reads the same legal-value sets.
 */

// ──────────────────────────────────────────────────────────────────────
// RC-219 / RC-220 — legal value sets
// ──────────────────────────────────────────────────────────────────────

export const LEGAL_ASSIGNMENT_MODES = Object.freeze(new Set([
  'native_assignee',   // RC-156: Prove7 inbox routing via FormTaskLifecycleWorkflow
  'external_api',      // Shape A — external POST to /steps/{stepId}/submit
  'approval_center',   // Legacy — pre-RC-156 Approval Center path
]));

export const LEGAL_ASSIGNEE_TYPES = Object.freeze(new Set([
  'USER',
  'GROUP',
  'ROLE',
  'DYNAMIC',           // RC-219 — runtime in RC-222
  'EXTERNAL_SUBJECT',  // RC-219 — runtime in RC-223
]));

export const LEGAL_DYNAMIC_RESOLVE_VIA = Object.freeze(new Set([
  'EMAIL', 'USER_ID', 'EXTERNAL_SUBJECT_REF',
]));

export const LEGAL_EXTERNAL_NOTIFY_VIA = Object.freeze(new Set([
  'WEBHOOK', 'SSE', 'NONE',
]));

/** Slices that ship the runtime for each new assigneeType. Surfaced to
 *  operators in the canvas issue panel so saving DYNAMIC/EXTERNAL_SUBJECT
 *  doesn't appear to be "broken" — they see "runtime ships in RC-222". */
const RUNTIME_SLICE_BY_TYPE = Object.freeze({
  DYNAMIC: 'RC-222',
  EXTERNAL_SUBJECT: 'RC-223',
});

// ──────────────────────────────────────────────────────────────────────
// Main entry — push issues for one node
// ──────────────────────────────────────────────────────────────────────

/**
 * Validate a single canvas node's form_task assignment block (if any) and
 * push 0+ issues onto the supplied array. The validator skips entirely
 * when:
 *   - n.type !== 'form_task' (caller can pass any node; we short-circuit)
 *   - n.config.assignment is absent (legacy path — those go through the
 *     Approval Center route by default, no inbox routing)
 *
 * Issue shape matches v3Validate/v4Validate's existing contract:
 *   { id, severity, code, nodeId, message, suggest }
 */
export function validateFormTaskAssignment(node, issues) {
  if (!node || node.type !== 'form_task') return;
  const assignment = node.config?.assignment;
  // `typeof [] === 'object'`, so the object test alone lets an array through
  // and the whole validator then runs against a malformed block, warning the
  // operator about an assignee for a node whose assignment shape is garbage.
  // An array is not an assignment block; treat it like an absent one.
  if (!assignment || typeof assignment !== 'object' || Array.isArray(assignment)) return;

  const nodeId = node.id;
  const label = node.label || node.id;

  validateMode(nodeId, label, assignment, issues);

  // The assignee block belongs to native_assignee and to nothing else:
  // external_api routes via the Shape A round-trip, approval_center via the
  // legacy approver list, and neither reads an assignee.
  //
  // native_assignee is OPT-IN BY EXPLICIT VALUE. CanvasDAGWorkflowImpl takes
  // the native branch only on `"native_assignee".equals(assignment.get("mode"))`
  // and falls through to the approval-center path for everything else, and
  // FormsCatalogueService.resolveAssignmentMode reports a missing or blank
  // mode as approval_center. An unset mode therefore executes as
  // approval_center.
  //
  // This read `mode != null && mode !== 'native_assignee'`, which inverted
  // that for the unset case: a form task carrying an assignment block with no
  // mode was validated AS native_assignee and told to configure an assignee
  // the executor will never read. Mirror the backend — require the explicit
  // value, exactly as the workflow does.
  if (assignment.mode !== 'native_assignee') return;
  validateAssignee(nodeId, label, assignment.assignee, issues);
}

// ──────────────────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────────────────

function validateMode(nodeId, label, assignment, issues) {
  const mode = assignment.mode;
  if (mode == null) return; // legacy / unset — defaults to approval_center on BE
  if (!LEGAL_ASSIGNMENT_MODES.has(mode)) {
    issues.push({
      id: `assignment-mode-${nodeId}`,
      severity: 'error',
      code: 'INVALID_ASSIGNMENT_MODE',
      nodeId,
      message: `Form task "${label}" has unknown assignment mode: "${mode}".`,
      suggest: `Set assignment.mode to one of: ${Array.from(LEGAL_ASSIGNMENT_MODES).join(', ')}.`,
    });
  }
}

function validateAssignee(nodeId, label, assignee, issues) {
  // Absent assignee block on native_assignee = the operator hasn't picked
  // yet (Designer just opened). This is a soft "configure me" warning,
  // NOT an error — RC-219 design lets the canvas save in a draft state
  // so the operator can iterate.
  if (!assignee || typeof assignee !== 'object') {
    issues.push({
      id: `assignment-assignee-missing-${nodeId}`,
      severity: 'warning',
      code: 'ASSIGNEE_NOT_CONFIGURED',
      nodeId,
      message: `Form task "${label}" uses native_assignee mode but has no assignee configured.`,
      suggest: 'Open the form in the Form Designer and pick an assignee.',
    });
    return;
  }

  const assigneeType = assignee.assigneeType;
  if (!assigneeType || !LEGAL_ASSIGNEE_TYPES.has(assigneeType)) {
    issues.push({
      id: `assignment-type-${nodeId}`,
      severity: 'error',
      code: 'INVALID_ASSIGNEE_TYPE',
      nodeId,
      message: `Form task "${label}" has unknown assigneeType: "${assigneeType || '(empty)'}".`,
      suggest: `Set assignment.assignee.assigneeType to one of: ${Array.from(LEGAL_ASSIGNEE_TYPES).join(', ')}.`,
    });
    return;
  }

  const ref = assignee.assigneeRef || {};
  validateRefShape(nodeId, label, assigneeType, ref, issues);
}

function validateRefShape(nodeId, label, assigneeType, ref, issues) {
  switch (assigneeType) {
    case 'USER':
      if (isBlank(ref.userId)) {
        pushRefMissing(nodeId, label, 'USER', 'userId', 'select a user', issues);
      }
      return;

    case 'GROUP':
      if (isBlank(ref.groupId)) {
        pushRefMissing(nodeId, label, 'GROUP', 'groupId', 'select a group', issues);
      }
      return;

    case 'ROLE':
      if (isBlank(ref.roleName)) {
        pushRefMissing(nodeId, label, 'ROLE', 'roleName', 'select a role', issues);
      }
      return;

    case 'DYNAMIC':
      validateDynamicRef(nodeId, label, ref, issues);
      emitRuntimeSliceNotice(nodeId, label, 'DYNAMIC', issues);
      return;

    case 'EXTERNAL_SUBJECT':
      validateExternalSubjectRef(nodeId, label, ref, issues);
      emitRuntimeSliceNotice(nodeId, label, 'EXTERNAL_SUBJECT', issues);
      return;

    default:
      // unreachable — assigneeType was already gated to LEGAL_ASSIGNEE_TYPES.
      return;
  }
}

// ── Per-mode helpers ──────────────────────────────────────────────────

function validateDynamicRef(nodeId, label, ref, issues) {
  // Shape rules mirror AssigneeRef.DynamicConfig canonical constructor +
  // AssigneeResolverService.validateDynamicShape.
  if (!ref.resolveVia || !LEGAL_DYNAMIC_RESOLVE_VIA.has(ref.resolveVia)) {
    issues.push({
      id: `assignee-ref-dynamic-via-${nodeId}`,
      severity: 'error',
      code: 'ASSIGNEE_REF_INVALID',
      nodeId,
      message: `Form task "${label}" DYNAMIC assignee has invalid resolveVia: "${ref.resolveVia || '(empty)'}".`,
      suggest: `Set assignee.assigneeRef.resolveVia to one of: ${Array.from(LEGAL_DYNAMIC_RESOLVE_VIA).join(', ')}.`,
    });
  }

  if (isBlank(ref.sourceExpression)) {
    issues.push({
      id: `assignee-ref-dynamic-expr-${nodeId}`,
      severity: 'error',
      code: 'ASSIGNEE_REF_INVALID',
      nodeId,
      message: `Form task "${label}" DYNAMIC assignee has no sourceExpression.`,
      suggest: 'Set assignee.assigneeRef.sourceExpression to a workflow-variable reference (e.g. ${claim_intake.output.requester_email}).',
    });
  } else if (!String(ref.sourceExpression).includes('${')) {
    // A literal value here = the operator picked DYNAMIC by mistake.
    issues.push({
      id: `assignee-ref-dynamic-literal-${nodeId}`,
      severity: 'error',
      code: 'ASSIGNEE_REF_INVALID',
      nodeId,
      message: `Form task "${label}" DYNAMIC sourceExpression must reference a workflow variable (got literal: "${ref.sourceExpression}").`,
      suggest: 'Wrap a variable reference: ${node.output.field}. Pure literals belong in a STATIC assignee mode.',
    });
  }
}

function validateExternalSubjectRef(nodeId, label, ref, issues) {
  // Shape rules mirror AssigneeRef.ExternalSubjectConfig canonical
  // constructor + AssigneeResolverService.validateExternalSubjectShape.
  if (isBlank(ref.stepId)) {
    issues.push({
      id: `assignee-ref-extsubj-step-${nodeId}`,
      severity: 'error',
      code: 'ASSIGNEE_REF_INVALID',
      nodeId,
      message: `Form task "${label}" EXTERNAL_SUBJECT assignee has no stepId.`,
      suggest: 'Set assignee.assigneeRef.stepId to a URL-safe identifier (e.g. upload_evidence).',
    });
  } else {
    const step = String(ref.stepId);
    if (step.includes('/') || step.includes(' ')) {
      issues.push({
        id: `assignee-ref-extsubj-stepfmt-${nodeId}`,
        severity: 'error',
        code: 'ASSIGNEE_REF_INVALID',
        nodeId,
        message: `Form task "${label}" EXTERNAL_SUBJECT stepId must be URL-safe (no slashes, no spaces). Got: "${step}".`,
        suggest: 'Use snake_case or kebab-case (e.g. upload_evidence, customer-signoff).',
      });
    }
  }

  if (!ref.notifyVia || !LEGAL_EXTERNAL_NOTIFY_VIA.has(ref.notifyVia)) {
    issues.push({
      id: `assignee-ref-extsubj-notify-${nodeId}`,
      severity: 'error',
      code: 'ASSIGNEE_REF_INVALID',
      nodeId,
      message: `Form task "${label}" EXTERNAL_SUBJECT assignee has invalid notifyVia: "${ref.notifyVia || '(empty)'}".`,
      suggest: `Set assignee.assigneeRef.notifyVia to one of: ${Array.from(LEGAL_EXTERNAL_NOTIFY_VIA).join(', ')}.`,
    });
  }
}

function emitRuntimeSliceNotice(nodeId, label, assigneeType, issues) {
  const slice = RUNTIME_SLICE_BY_TYPE[assigneeType];
  if (!slice) return;
  issues.push({
    id: `assignee-runtime-pending-${nodeId}`,
    severity: 'info',
    code: 'ASSIGNEE_RUNTIME_PENDING',
    nodeId,
    message: `Form task "${label}" uses ${assigneeType} assignment. Shape is valid; runtime support ships in ${slice}.`,
    suggest: 'Saving is allowed for now; executing this workflow will surface a runtime error until the slice lands.',
  });
}

// ── Tiny utils ────────────────────────────────────────────────────────

function isBlank(v) {
  return v == null || String(v).trim() === '';
}

function pushRefMissing(nodeId, label, typeName, refKey, fixHint, issues) {
  issues.push({
    id: `assignee-ref-${typeName.toLowerCase()}-${nodeId}`,
    severity: 'error',
    code: 'ASSIGNEE_REF_MISSING',
    nodeId,
    message: `Form task "${label}" assignee is ${typeName} but no ${refKey} is set.`,
    suggest: `Open the Form Designer assignee picker and ${fixHint}.`,
  });
}
